import type { MedVisitCitation } from "@/services/medChronServiceV2.types";
import type { MedVisit } from "@/types/medchron";
import { z } from "zod";

import type {
  CaseFileOption,
  CitationDraftRow,
  CitationDraftValidation,
  CitationRowErrors,
} from "../types";

const PAGE_ERROR = "Enter a page number of 1 or more";
// Global cap on any entered page; per-file limits come from buildPageLimits.
const MAX_PAGE = 100_000;

const pageSchema = z
  .string()
  .trim()
  .regex(/^\d+$/, PAGE_ERROR)
  .transform(Number)
  .pipe(z.number().min(1, PAGE_ERROR).max(MAX_PAGE, `Enter a page number of ${MAX_PAGE} or less`));

const buildCitationRowSchema = (
  validFileIds: ReadonlySet<string>,
  pageLimits: ReadonlyMap<string, number>
) =>
  z
    .object({
      file_id: z.string().refine((fileId) => validFileIds.has(fileId), "Select a file"),
      start_page: pageSchema,
      end_page: pageSchema,
    })
    .refine((row) => row.start_page <= row.end_page, {
      message: "End page must not be before start page",
      path: ["end_page"],
    })
    .superRefine((row, ctx) => {
      const pageLimit = pageLimits.get(row.file_id);
      if (pageLimit === undefined) return;
      for (const field of ["start_page", "end_page"] as const) {
        if (row[field] > pageLimit) {
          ctx.addIssue({
            code: "custom",
            message: `File has ${pageLimit} ${pageLimit === 1 ? "page" : "pages"}`,
            path: [field],
          });
        }
      }
    });

/**
 * Maps each PDF with a known page count to that count.
 * - Mirrors the server, which bounds pages only for PDFs; other file types hold estimated counts.
 */
export const buildPageLimits = (files: CaseFileOption[]): Map<string, number> =>
  new Map(
    files.flatMap((file): [string, number][] =>
      file.filename?.toLowerCase().endsWith(".pdf") && file.page_count && file.page_count > 0
        ? [[file.file_id, file.page_count]]
        : []
    )
  );

export const createCitationDraftRow = (fileId = ""): CitationDraftRow => ({
  key: crypto.randomUUID(),
  file_id: fileId,
  start_page: "",
  end_page: "",
});

export const toCitationDraftRows = (citations: MedVisitCitation[]): CitationDraftRow[] =>
  citations.map((citation) => ({
    key: crypto.randomUUID(),
    file_id: citation.file_id,
    start_page: String(citation.start_page),
    end_page: String(citation.end_page),
  }));

/**
 * Validates draft rows and parses them into citations.
 * - A blank end page means a single-page citation, so it takes the start page.
 * - Rows in unmodifiedRows (matched by identity) skip the page-limit check so legacy data never blocks other edits.
 */
export const validateCitationDraftRows = (
  rows: CitationDraftRow[],
  validFileIds: ReadonlySet<string>,
  pageLimits: ReadonlyMap<string, number> = new Map(),
  unmodifiedRows: ReadonlySet<CitationDraftRow> = new Set()
): CitationDraftValidation => {
  const rowSchema = buildCitationRowSchema(validFileIds, pageLimits);
  const unmodifiedRowSchema = buildCitationRowSchema(validFileIds, new Map());
  const citations: MedVisitCitation[] = [];
  const rowErrors = rows.map((row): CitationRowErrors => {
    const result = (unmodifiedRows.has(row) ? unmodifiedRowSchema : rowSchema).safeParse({
      ...row,
      end_page: row.end_page.trim() === "" ? row.start_page : row.end_page,
    });
    if (result.success) {
      citations.push(result.data);
      return {};
    }
    const errors: CitationRowErrors = {};
    for (const issue of result.error.issues) {
      const path = issue.path[0];
      // Issues outside the three fields land on end_page so an invalid row is never left unmarked.
      const field = path === "file_id" || path === "start_page" ? path : "end_page";
      errors[field] ??= issue.message;
    }
    return errors;
  });
  return { isValid: citations.length === rows.length, citations, rowErrors };
};

export const areCitationListsEqual = (
  current: MedVisitCitation[],
  next: MedVisitCitation[]
): boolean =>
  current.length === next.length &&
  current.every(
    (citation, index) =>
      citation.file_id === next[index].file_id &&
      citation.start_page === next[index].start_page &&
      citation.end_page === next[index].end_page
  );

/** Sorts by file then start page and merges overlapping or adjacent ranges within a file. */
const mergeCitationRanges = (citations: MedVisitCitation[]): MedVisitCitation[] =>
  [...citations]
    .sort(
      (left, right) =>
        left.file_id.localeCompare(right.file_id) || left.start_page - right.start_page
    )
    .reduce<MedVisitCitation[]>((merged, citation) => {
      const last = merged[merged.length - 1];
      if (last && last.file_id === citation.file_id && citation.start_page <= last.end_page + 1) {
        last.end_page = Math.max(last.end_page, citation.end_page);
      } else {
        merged.push({ ...citation });
      }
      return merged;
    }, []);

/** The visit's cited pages as merged ranges. */
export const toCanonicalCitations = (visit: MedVisit): MedVisitCitation[] =>
  mergeCitationRanges(visit.citations);
